/**
 * #1123 — durable fix for the recurring Gutschrift-Check canvas
 * spacing drift. Subscribes to xyflow v12's internal ``nodeLookup``
 * and exposes per-node measured heights as a stable Map.
 *
 * xyflow v12 attaches a ResizeObserver to every rendered node and
 * writes the observed dimensions onto
 * ``nodeLookup.get(nodeId).measured.{width,height}``. We re-expose
 * just the heights as ``Map<nodeId, height>`` so the MatchGraph
 * layout pass can read each card's true height instead of the
 * brittle, hand-rolled estimator the rest of the codebase historically
 * relied on (see auto-memory
 * ``reference_gutschrift_check_canvas_estimated_layout`` and the
 * #842/#889/#892/#914/#915/#917 timeline in the #1123 context manifest).
 *
 * Heights are populated asynchronously — they're ``undefined`` on
 * first paint and become defined once each tile mounts and the
 * ResizeObserver fires. Consumers MUST fall back to a baseline for
 * any missing entry so the first frame is never blank / mis-positioned
 * (see ``stackYs`` and ``stackHeight`` in ``MatchGraph.tsx``).
 *
 * The equality function below compares Map size + per-key values so
 * the returned reference is stable across xyflow store ticks that
 * don't actually change any measurement (selection / hover / pan
 * updates all bump the store). That stability is what lets the
 * MatchGraph ``useMemo`` skip re-running when heights are unchanged.
 *
 * Hook must be called inside a ``<ReactFlowProvider>`` — see
 * ``MatchGraph``'s outer wrapper.
 */
import { useStore, type ReactFlowState } from '@xyflow/react';

const selector = (state: ReactFlowState): Map<string, number> => {
  const map = new Map<string, number>();
  state.nodeLookup.forEach((node, id) => {
    const h = node.measured?.height;
    if (typeof h === 'number' && Number.isFinite(h)) {
      map.set(id, h);
    }
  });
  return map;
};

const heightMapEqual = (
  a: Map<string, number>,
  b: Map<string, number>,
): boolean => {
  if (a.size !== b.size) return false;
  for (const [k, v] of a) {
    if (b.get(k) !== v) return false;
  }
  return true;
};

/**
 * Returns the current measured-height map for every xyflow node in
 * the surrounding ``<ReactFlowProvider>``. Reference identity is
 * stable across store ticks that don't change any measurement.
 */
export function useMeasuredHeights(): Map<string, number> {
  return useStore(selector, heightMapEqual);
}
